import React, { useState } from 'react'
import { Button, makeStyles, MessageBar, MessageBarBody, MessageBarTitle, shorthands, tokens } from '@fluentui/react-components'
import { Add20Regular, AddCircle24Regular } from '@fluentui/react-icons'
import { t } from '../../../i18n/i18n'
import { RiskAnalysis, RiskAnalysisStatus, SWOT } from '../../../api/schemas/schema'
import { List } from '../../common/list/List'
import { getFilteredSWOTs, getSWOTIdentifyColumns } from '../../../helpers/swotHelper'
import { useSearchParams } from 'react-router-dom'
import { TypeFilter } from './TypeFilter'
import { ModalSWOT } from './ModalSWOT'
import { PhoneHeader } from '../../common/header/PhoneHeader'
import { Text } from '../../common/text/Text'
import { SwotTypeBadge } from '../RiskBadges'
import { IItemState } from '../../../interfaces/IItemState'
import { compareByDate } from '../../../helpers/stringHelper'

const useStyles = makeStyles({
   hideInPhone: {
      '@media screen and (max-width: 640px)': {
         display: 'none',
      },
   },
   showOnlyPhone: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         display: 'block',
      },
   },
   text: {
      ...shorthands.margin(tokens.spacingHorizontalS, 0),
   },
   spaceBtw: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
   },
   btnFlex: {
      display: 'flex',
      gap: tokens.spacingHorizontalS,
   },
   phoneItemHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
   },
   ellipsis: {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
      WebkitLineClamp: 1,
      '@media screen and (max-width: 640px)': {
         WebkitLineClamp: 2,
      },
   },
})
type SWOTIdentifyingProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
}
export const SWOTIdentifying = ({ setRiskAnalysisState, analysisState }: SWOTIdentifyingProps) => {
   const classes = useStyles()
   const [modalOpen, setModalOpen] = useState<boolean>(false)
   const [searchParams] = useSearchParams()
   const [selectedItem, setSelectedItem] = useState<SWOT>(null)
   const selectedType = searchParams.get('type')

   const analysis = analysisState.item
   const filteredSWOTs = getFilteredSWOTs(analysis.swoTs, selectedType).sort((a, b) => compareByDate(b.created, a.created))
   const disabled = analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed
   const noSwots = analysis.swoTs.length === 0

   const onRowClick = (item: SWOT) => {
      setSelectedItem(item)
      setModalOpen(true)
   }
   const title = t('Identify')
   return (
      <>
         <PhoneHeader
            title={title}
            action={
               <Button style={{ width: '100%' }} onClick={() => setModalOpen(true)} appearance="primary" icon={<Add20Regular />} disabled={disabled}>
                  {t('Add')}
               </Button>
            }
         />
         <TypeFilter
            title={title}
            disabled={noSwots}
            action={
               <Button
                  onClick={() => {
                     setModalOpen(true)
                  }}
                  appearance="primary"
                  disabled={disabled}
                  icon={<AddCircle24Regular />}
               >
                  {t('Add')}
               </Button>
            }
         />
         {noSwots ? (
            <MessageBar>
               <MessageBarBody>
                  <MessageBarTitle>{t('TimeToAddYourFirstSWOT')}</MessageBarTitle>
               </MessageBarBody>
            </MessageBar>
         ) : (
            <>
               <List
                  columnSizingOptions={{ title: { idealWidth: 400 } }}
                  resizableColumns
                  columns={getSWOTIdentifyColumns(classes.ellipsis)}
                  items={filteredSWOTs}
                  onRowClick={onRowClick}
                  renderPhoneBodyItem={(item: SWOT) => (
                     <>
                        <span className={classes.phoneItemHeader}>
                           <Text weight="bold">{item.title}</Text>
                           <SwotTypeBadge type={item.type} />
                        </span>
                        <Text className={classes.ellipsis}>{item.description}</Text>
                     </>
                  )}
               />
            </>
         )}
         <ModalSWOT
            selectedItem={selectedItem}
            open={modalOpen}
            close={() => {
               setModalOpen(false)
               setSelectedItem(null)
            }}
            analysisState={analysisState}
            setRiskAnalysisState={setRiskAnalysisState}
         />
      </>
   )
}
